import React from "react";
import { Card } from "@mui/material";
import { Branch } from "../types/branch";
import BranchDropdownMenu from "./BranchDropdownMenu";
import { formatDate } from "../utils/validation";
import { MapPin, CalendarDays, Building2, Phone, User } from "lucide-react";

interface BranchCardProps {
  branch: Branch;
  onEdit: (branch: Branch) => void;
  onDelete: (branchId: string) => void;
  showDeletedBranches: boolean;
}

const FallbackText = ({ text }: { text: string }) => <span className="text-gray-400 italic">{text}</span>;

const BranchCard: React.FC<BranchCardProps> = ({ branch, onEdit, onDelete, showDeletedBranches }) => {
  const { Branch_name, Branch_Opening_date, Address, contact_person, branch_number, _id } = branch;

  const branchId = typeof _id === "string" ? _id : _id.$oid;

  const addressLines = [
    [Address.address1, Address.street].filter(Boolean).join(", "),
    [Address.selectedCountry, Address.selectedState, Address.selectedCity].filter(Boolean).join(", "),
    Address.Pincode,
  ].filter(Boolean);

  const iconClass = "w-4 h-4";
  const iconMap = {
    "Branch Name": <Building2 className={`${iconClass} text-indigo-500`} />,
    "Opening Date": <CalendarDays className={`${iconClass} text-green-600`} />,
    "Contact Person": <User className={`${iconClass} text-blue-500`} />,
    "Contact Number": <Phone className={`${iconClass} text-purple-500`} />,
    Address: <MapPin className={`${iconClass} text-red-500 mt-1`} />,
  };

  const getValue = (value: string | null | undefined, fallback: string) => value || <FallbackText text={fallback} />;

  const fields: { label: string; value: React.ReactNode }[] = [
    { label: "Branch Name", value: getValue(Branch_name, "Not provided") },
    { label: "Opening Date", value: Branch_Opening_date ? formatDate(Branch_Opening_date) : <FallbackText text="Not provided" /> },
    { label: "Contact Person", value: getValue(contact_person, "Not assigned") },
    { label: "Contact Number", value: getValue(branch_number, "Not available") },
    {
      label: "Address",
      value: addressLines.length ? (
        <div className="text-gray-800 text-sm leading-snug truncate">
          {addressLines.map((line, idx) => (
            <div key={idx}>{line}</div>
          ))}
        </div>
      ) : (
        <FallbackText text="No address provided" />
      ),
    },
  ];

  return (
    <Card className="relative cursor-pointer hover:shadow-lg transition p-5 rounded-lg border border-gray-500 bg-white h-[250px]">
      <div className="absolute top-3 right-3" onClick={(e) => e.stopPropagation()}>
        <BranchDropdownMenu 
          branchId={branchId} 
          onEdit={() => onEdit(branch)} 
          onDelete={() => onDelete(branchId)} 
          showDeletedBranches={showDeletedBranches} />
      </div>

      <div className="space-y-2">
        {fields.map(({ label, value }) => (
          <div key={label} className="flex items-start gap-2">
            {iconMap[label as keyof typeof iconMap]}
            <h4 className="font-bold">
              {label}: <span className="font-normal text-gray-800">{value}</span>
            </h4>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default BranchCard;
